import { FavoriteView, Filter } from '@iot-platform/models/common';
import { Action, createReducer, on } from '@ngrx/store';
import { FavoriteViewsActions } from '../actions';

export const favoriteViewsUiFeatureKey = 'favoriteViewsUi';

interface CurrentFavoriteViews {
  sites: string;
  assets: string;
  devices: string;
  assetEvents: string;
  deviceEvents: string;
}

interface CurrentFilters {
  sites: Filter[];
  assets: Filter[];
  devices: Filter[];
  assetEvents: Filter[];
  deviceEvents: Filter[];
}

export interface State {
  currentFavoriteViews: CurrentFavoriteViews;
  currentFilters: CurrentFilters;

  loading: boolean;
  loaded: boolean;
  error?: unknown;
}

export const initialState: State = {
  currentFavoriteViews: { sites: null, assets: null, devices: null, assetEvents: null, deviceEvents: null },
  currentFilters: { sites: [], assets: [], devices: [], assetEvents: [], deviceEvents: [] },
  loading: false,
  loaded: false
};

const favoriteViewsReducer = createReducer(
  initialState,
  on(
    FavoriteViewsActions.addFavoriteView,
    FavoriteViewsActions.loadFavoriteViews,
    FavoriteViewsActions.updateFavoriteView,
    FavoriteViewsActions.addFavoriteViewFromMV,
    FavoriteViewsActions.updateFavoriteViewFromMV,
    (state): State => ({
      ...state,
      loaded: false,
      loading: true
    })
  ),
  on(
    FavoriteViewsActions.addFavoriteViewError,
    FavoriteViewsActions.loadFavoriteViewsError,
    FavoriteViewsActions.updateFavoriteViewError,
    FavoriteViewsActions.addFavoriteViewFromMVError,
    FavoriteViewsActions.updateFavoriteViewFromMVError,
    (state, { error }): State => ({ ...state, loaded: false, loading: false, error })
  ),
  on(
    FavoriteViewsActions.addFavoriteViewSuccess,
    FavoriteViewsActions.updateFavoriteViewSuccess,
    FavoriteViewsActions.addFavoriteViewFromMVSuccess,
    FavoriteViewsActions.updateFavoriteViewFromMVSuccess,
    (state, { favoriteView }): State => ({
      ...state,
      loaded: true,
      loading: false,
      currentFavoriteViews: setCurrentFavoriteViews(state.currentFavoriteViews, favoriteView.masterView, favoriteView),
      currentFilters: setCurrentFilters(state.currentFilters, favoriteView.masterView, favoriteView.filters)
    })
  ),
  on(
    FavoriteViewsActions.loadFavoriteViewsSuccess,
    FavoriteViewsActions.getCountForFavoriteViewSuccess,
    (state): State => ({
      ...state,
      loaded: true,
      loading: false
    })
  ),
  on(
    FavoriteViewsActions.setCurrentFavoriteView,
    (state, { masterView, favoriteView }): State => ({
      ...state,
      currentFavoriteViews: setCurrentFavoriteViews(state.currentFavoriteViews, masterView, favoriteView),
      currentFilters: favoriteView
        ? setCurrentFilters(state.currentFilters, masterView, favoriteView.filters)
        : setCurrentFilters(state.currentFilters, masterView, [])
    })
  ),
  on(
    FavoriteViewsActions.setCurrentFilters,
    (state, { masterView, filters }): State => ({
      ...state,
      currentFilters: setCurrentFilters(state.currentFilters, masterView, filters)
    })
  ),
  on(FavoriteViewsActions.resetState, (): State => initialState)
);

export function reducer(state: State | undefined, action: Action) {
  return favoriteViewsReducer(state, action);
}

function setCurrentFavoriteViews(currentFavoriteViews: CurrentFavoriteViews, masterView: string, favoriteView: FavoriteView) {
  const newCurrentFavoriteView = { ...currentFavoriteViews };
  switch (masterView) {
    case 'sites':
      newCurrentFavoriteView.sites = favoriteView ? favoriteView.id : null;
      break;
    case 'assets':
      newCurrentFavoriteView.assets = favoriteView ? favoriteView.id : null;
      break;
    case 'devices':
    case 'dalia-devices':
      newCurrentFavoriteView.devices = favoriteView ? favoriteView.id : null;
      break;
    case 'asset-events':
      newCurrentFavoriteView.assetEvents = favoriteView ? favoriteView.id : null;
      break;
    case 'device-events':
      newCurrentFavoriteView.deviceEvents = favoriteView ? favoriteView.id : null;
      break;
    default:
  }

  return newCurrentFavoriteView;
}

function setCurrentFilters(currentFilters: CurrentFilters, masterView: string, filters: Filter[]) {
  const newCurrentFilters = { ...currentFilters };
  switch (masterView) {
    case 'sites':
      newCurrentFilters.sites = filters;
      break;
    case 'assets':
      newCurrentFilters.assets = filters;
      break;
    case 'devices':
    case 'dalia-devices':
      newCurrentFilters.devices = filters;
      break;
    case 'asset-events':
      newCurrentFilters.assetEvents = filters;
      break;
    case 'device-events':
      newCurrentFilters.deviceEvents = filters;
      break;
    default:
  }
  return newCurrentFilters;
}

export const getLoading = (state: State) => state.loading;
export const getLoaded = (state: State) => state.loaded;
export const getError = (state: State) => state.error;

export const getCurrentFavoriteViews = (state: State) => state.currentFavoriteViews;
export const getCurrentFavoriteViewIdForAssets = (state: State) => state.currentFavoriteViews.assets;
export const getCurrentFavoriteViewIdForSites = (state: State) => state.currentFavoriteViews.sites;
export const getCurrentFavoriteViewIdForDevices = (state: State) => state.currentFavoriteViews.devices;
export const getCurrentFavoriteViewIdForAssetEvents = (state: State) => state.currentFavoriteViews.assetEvents;
export const getCurrentFavoriteViewIdForDeviceEvents = (state: State) => state.currentFavoriteViews.deviceEvents;

export const getCurrentFilters = (state: State) => state.currentFilters;
export const getCurrentFiltersForAssets = (state: State) => state.currentFilters.assets;
export const getCurrentFiltersForSites = (state: State) => state.currentFilters.sites;
export const getCurrentFiltersForDevices = (state: State) => state.currentFilters.devices;
export const getCurrentFiltersForAssetEvents = (state: State) => state.currentFilters.assetEvents;
export const getCurrentFiltersForDeviceEvents = (state: State) => state.currentFilters.deviceEvents;
